import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Copy, Save, CalendarClock, Image as ImageIcon, Store } from "lucide-react";
import { useTr } from "@/i18n/translator";
import BrandSelector from "@/components/BrandSelector";

const NETWORKS = [
  { id: "twitter", label: "Twitter / X" },
  { id: "facebook", label: "Facebook" },
  { id: "instagram", label: "Instagram" },
  { id: "linkedin", label: "LinkedIn" },
  { id: "tiktok", label: "TikTok" },
];

const TYPES = [
  { id: "post", label: "Post" },
  { id: "carrusel", label: "Carrusel" },
  { id: "hilo", label: "Hilo" },
  { id: "script_reel", label: "Script Reel" },
  { id: "caption", label: "Caption" },
];

export default function Generator() {
  const tr = useTr();
  const [network, setNetwork] = useState("instagram");
  const [type, setType] = useState("post");
  const [topic, setTopic] = useState("");
  const [variants, setVariants] = useState(3);
  const [extra, setExtra] = useState("");
  const [abTest, setAbTest] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [image, setImage] = useState(null);
  const [brandId, setBrandId] = useState("");
  const [abPreferred, setAbPreferred] = useState(null);
  const [bestTime, setBestTime] = useState(null);

  // Carga el "smart pick" preferido para la marca activa
  useEffect(() => {
    if (!brandId) { setAbPreferred(null); return; }
    api.get(`/brands/${brandId}/ab-stats`).then(({ data }) => {
      setAbPreferred(data.preferred_style && data.confidence > 0.3 ? data : null);
    }).catch(() => setAbPreferred(null));
  }, [brandId]);

  // Carga el mejor horario al cambiar marca o red
  useEffect(() => {
    if (!brandId || !network) { setBestTime(null); return; }
    api.get(`/brands/${brandId}/best-times`, { params: { network } })
      .then(({ data }) => setBestTime(data))
      .catch(() => setBestTime(null));
  }, [brandId, network]);

  const generate = async () => {
    if (!topic.trim()) {
      toast.error(tr("Describe el tema del post"));
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/generate/post", {
        network,
        content_type: type,
        topic,
        n_variants: abTest ? 2 : variants,
        extra_instructions: extra,
        brand_id: brandId || null,
        ab_test: abTest,
        ui_lang: (localStorage.getItem("cia_lang") || "es"),
      });
      setResult(data);
      toast.success(abTest ? "Variantes A/B listas — elige la ganadora" : `Generadas ${data.variants.length} variantes`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al generar");
    } finally {
      setLoading(false);
    }
  };

  const generateImage = async () => {
    if (!topic.trim()) return toast.error(tr("Describe el tema para la imagen"));
    setGeneratingImage(true);
    try {
      const { data } = await api.post("/generate/image", {
        prompt: topic,
        brand_aware: true,
        brand_id: brandId || null,
      });
      setImage(data);
      toast.success("Imagen generada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al generar imagen");
    } finally {
      setGeneratingImage(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado");
  };

  const saveToBank = async (variant) => {
    try {
      await api.post("/bank", {
        title: topic.slice(0, 80),
        body: variant,
        network,
        hashtags: result?.hashtags || [],
        category: type,
        is_favorite: false,
      });
      toast.success(tr("Guardado en el banco"));
    } catch {
      toast.error("No se pudo guardar");
    }
  };

  const schedule = async (variant, idx, whenIso = null) => {
    const when = whenIso || new Date(Date.now() + 24 * 3600 * 1000).toISOString();
    try {
      await api.post("/posts", {
        body: variant + (result?.hashtags?.length ? "\n\n" + result.hashtags.join(" ") : ""),
        network,
        scheduled_at: when,
        status: "pending",
        hashtags: result?.hashtags || [],
        image_base64: image?.image_base64 || null,
        image_id: image?.id || null,
        generation_id: result?.id || null,
        ab_variant_index: result?.ab_test ? idx : null,
        ab_variant_style: result?.ab_test ? (result?.variant_styles?.[idx] || null) : null,
      });
      const friendly = whenIso
        ? `Programado para ${new Date(when).toLocaleString("es-MX", { weekday: "long", hour: "numeric", minute: "2-digit", hour12: true })}`
        : tr("Programado para mañana a esta hora");
      toast.success(friendly);
    } catch {
      toast.error("Error al programar");
    }
  };

  const saveAsTemplate = async (variant) => {
    const name = window.prompt("Nombre para la plantilla:", topic.slice(0, 60));
    if (!name) return;
    const isPublic = window.confirm("¿Hacer pública en el Marketplace?\n\nOK = pública (gratis) · Cancelar = privada (solo tú)");
    try {
      await api.post("/templates", {
        name,
        category: type,
        industry: "todos",
        platforms: [network],
        language: "es-MX",
        body: variant,
        hashtags: result?.hashtags || [],
        image_url: image?.image_base64 ? `data:image/png;base64,${image.image_base64}` : "",
        is_public: isPublic,
        price_usd: 0,
      });
      toast.success(isPublic ? "Plantilla publicada en el Marketplace" : "Plantilla guardada (privada)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar plantilla");
    }
  };

  // Load draft from marketplace if present
  useEffect(() => {
    const raw = sessionStorage.getItem("cia_template_draft");
    if (raw) {
      try {
        const d = JSON.parse(raw);
        if (d.body) setTopic(d.body.slice(0, 280));
        if (d.platforms?.[0]) setNetwork(d.platforms[0]);
        toast.info(tr("Plantilla cargada · edita y regenera"));
        sessionStorage.removeItem("cia_template_draft");
      } catch {}
    }
  }, []);

  return (
    <div className="space-y-6" data-testid="generator-page">
      <div>
        <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("MISIÓN · GENERACIÓN")}</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">{tr("Generador de contenido IA")}</h1>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Form */}
        <div className="lg:col-span-2 bg-white border border-[#0A0A0A] p-6 space-y-5">
          <BrandSelector value={brandId} onChange={setBrandId} testid="gen-brand" />

          <div>
            <div className="font-mono-mark text-[10px] text-[#5C5C5C] mb-2">RED SOCIAL</div>
            <div className="grid grid-cols-3 gap-2">
              {NETWORKS.map((n) => (
                <button
                  key={n.id}
                  onClick={() => setNetwork(n.id)}
                  className={`px-2 py-2 font-mono-mark text-[10px] border ${network === n.id ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#0A0A0A] hover:bg-[#F4F4F0]"}`}
                  data-testid={`gen-net-${n.id}`}
                >
                  {n.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="font-mono-mark text-[10px] text-[#5C5C5C] mb-2">{tr("TIPO DE CONTENIDO")}</div>
            <div className="grid grid-cols-3 gap-2">
              {TYPES.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setType(t.id)}
                  className={`px-2 py-2 font-mono-mark text-[10px] border ${type === t.id ? "bg-[#E53935] text-white border-[#E53935]" : "bg-white border-[#0A0A0A] hover:bg-[#F4F4F0]"}`}
                  data-testid={`gen-type-${t.id}`}
                >
                  {tr(t.label)}
                </button>
              ))}
            </div>
          </div>

          <label className="block">
            <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("TEMA / BRIEF")}</span>
            <textarea
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              rows={4}
              placeholder={tr("Ej: Lanzamiento de nuevo café frío para oficina en CDMX")}
              className="w-full mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]"
              data-testid="gen-topic"
            />
          </label>

          <label className="block">
            <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("VARIANTES")}</span>
            <input
              type="number"
              min={1}
              max={5}
              value={variants}
              onChange={(e) => setVariants(Number(e.target.value))}
              disabled={abTest}
              className="w-full mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm disabled:opacity-50"
              data-testid="gen-variants"
            />
          </label>

          <label className="flex items-start gap-3 bg-[#FFD600] border border-[#0A0A0A] p-3 cursor-pointer">
            <input
              type="checkbox"
              checked={abTest}
              onChange={(e) => setAbTest(e.target.checked)}
              className="mt-0.5"
              data-testid="gen-abtest"
            />
            <span className="font-mono-mark text-[10px] leading-tight">
              MODO A/B TEST<br />
              <span className="font-mono-data text-[10px] text-[#5C5C5C] normal-case tracking-normal">
                Genera 2 variantes con estilos contrastantes (directo vs narrativo) para comparar engagement.
              </span>
            </span>
          </label>

          {abTest && abPreferred && (
            <div className="bg-[#16A34A] text-white border border-[#0A0A0A] p-3 font-mono-data text-xs" data-testid="gen-ab-recommendation">
              <span className="font-mono-mark text-[10px]">SMART PICK</span><br />
              Para esta marca, el estilo <b>{abPreferred.preferred_style}</b> ha sido elegido el {Math.round((abPreferred.by_style?.[abPreferred.preferred_style] || 0) / Math.max(abPreferred.total_ab_posts, 1) * 100)}% de las veces.
            </div>
          )}

          {bestTime && (
            <div className="bg-[#0A0A0A] text-[#FFD600] border border-[#0A0A0A] p-3 font-mono-data text-xs" data-testid="gen-best-time">
              <span className="font-mono-mark text-[10px]">
                MEJOR HORA · {bestTime.source === "history" ? "DATA REAL" : "INDUSTRIA"}
              </span><br />
              Publica los <b>{bestTime.next_slot.weekday_label}</b> a las <b>{bestTime.next_slot.hour_label}</b> en {network}.
              {bestTime.source === "industry" && (
                <span className="text-[#A0A0A0]"> · cuando tengas {bestTime.min_history_required}+ posts publicados, este número se calcula con tu propia data.</span>
              )}
            </div>
          )}

          <label className="block">
            <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("INSTRUCCIONES EXTRA")}</span>
            <input
              type="text"
              value={extra}
              onChange={(e) => setExtra(e.target.value)}
              placeholder="Ej: Mencionar descuento 15%"
              className="w-full mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm"
              data-testid="gen-extra"
            />
          </label>

          <button
            onClick={generate}
            disabled={loading}
            className="w-full bg-[#0A0A0A] text-white px-4 py-3 font-mono-mark text-xs hover-brutal border border-[#0A0A0A] flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="gen-submit"
          >
            <Sparkles className="w-4 h-4" />
            {loading ? "GENERANDO…" : "GENERAR"}
          </button>

          <button
            onClick={generateImage}
            disabled={generatingImage}
            className="w-full bg-white text-[#0A0A0A] px-4 py-3 font-mono-mark text-xs border border-[#0A0A0A] flex items-center justify-center gap-2 hover:bg-[#0A0A0A] hover:text-white disabled:opacity-50"
            data-testid="gen-image"
          >
            <ImageIcon className="w-4 h-4" />
            {generatingImage ? tr("CREANDO IMAGEN…") : tr("GENERAR IMAGEN")}
          </button>
        </div>

        {/* Results */}
        <div className="lg:col-span-3 space-y-4">
          {image && (
            <div className="bg-white border border-[#0A0A0A] p-4">
              <div className="font-mono-mark text-[10px] text-[#5C5C5C] mb-3">{tr("IMAGEN GENERADA · IA VISUAL")}</div>
              <img src={`data:image/png;base64,${image.image_base64}`} alt="generada" className="w-full max-h-96 object-contain border border-[#0A0A0A]" data-testid="gen-image-preview" />
            </div>
          )}

          {!result && !loading && (
            <div className="bg-white border border-dashed border-[#0A0A0A] p-10 text-center">
              <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("ESPERANDO ÓRDENES")}</div>
              <p className="mt-3 text-sm">{tr("Elige red, tipo y describe el tema. La IA generará variantes listas para publicar.")}</p>
            </div>
          )}

          {loading && (
            <div className="bg-white border border-[#0A0A0A] p-8 space-y-3">
              <div className="h-4 bg-[#E5E5E5] redact-pulse w-2/3" />
              <div className="h-4 bg-[#E5E5E5] redact-pulse w-full" />
              <div className="h-4 bg-[#E5E5E5] redact-pulse w-4/5" />
            </div>
          )}

          {result?.variants?.map((v, i) => {
            const style = result?.ab_test ? (result?.variant_styles?.[i] || `Variante ${i+1}`) : null;
            const isPreferred = result?.ab_test && abPreferred?.preferred_style === style;
            return (
            <div key={i} className={`bg-white border p-6 ${isPreferred ? "border-[#16A34A] border-2" : "border-[#0A0A0A]"}`} data-testid={`gen-variant-${i}`}>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-mono-mark text-[10px] text-[#5C5C5C]">
                    {result?.ab_test ? `VARIANTE ${i === 0 ? "A" : "B"}` : `VARIANTE ${String(i + 1).padStart(2, "0")}`}
                  </span>
                  {style && (
                    <span className={`font-mono-mark text-[9px] px-2 py-0.5 border border-[#0A0A0A] uppercase ${i === 0 ? "bg-[#FFD600]" : "bg-[#0A0A0A] text-[#FFD600]"}`}>
                      {style}
                    </span>
                  )}
                  {isPreferred && (
                    <span className="font-mono-mark text-[9px] bg-[#16A34A] text-white px-2 py-0.5 border border-[#0A0A0A]">
                      ⭐ RECOMENDADA
                    </span>
                  )}
                </div>
                <span className="font-mono-mark text-[10px] text-[#E53935]">{network.toUpperCase()}</span>
              </div>
              <p className="text-base whitespace-pre-wrap">{v}</p>
              {result.hashtags?.length > 0 && (
                <div className="mt-3 font-mono-data text-sm text-[#E53935]">{result.hashtags.join(" ")}</div>
              )}
              <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-[#0A0A0A]">
                <button onClick={() => copy(v)} className="font-mono-mark text-[10px] px-3 py-2 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white flex items-center gap-1" data-testid={`gen-copy-${i}`}>
                  <Copy className="w-3 h-3" /> {tr("COPIAR")}
                </button>
                <button onClick={() => saveToBank(v)} className="font-mono-mark text-[10px] px-3 py-2 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white flex items-center gap-1" data-testid={`gen-save-${i}`}>
                  <Save className="w-3 h-3" /> {tr("GUARDAR")}
                </button>
                <button onClick={() => saveAsTemplate(v)} className="font-mono-mark text-[10px] px-3 py-2 border border-[#0A0A0A] hover:bg-[#FFD600] flex items-center gap-1" data-testid={`gen-template-${i}`}>
                  <Store className="w-3 h-3" /> PLANTILLA
                </button>
                <button onClick={() => schedule(v, i)} className="font-mono-mark text-[10px] bg-[#E53935] text-white px-3 py-2 border border-[#E53935] flex items-center gap-1" data-testid={`gen-schedule-${i}`}>
                  <CalendarClock className="w-3 h-3" /> {tr("PROGRAMAR")}
                </button>
                {bestTime && (
                  <button
                    onClick={() => schedule(v, i, bestTime.next_slot.iso)}
                    className="font-mono-mark text-[10px] bg-[#FFD600] text-[#0A0A0A] px-3 py-2 border border-[#0A0A0A] flex items-center gap-1"
                    data-testid={`gen-schedule-best-${i}`}
                    title={`${bestTime.next_slot.weekday_label} ${bestTime.next_slot.hour_label}`}
                  >
                    <CalendarClock className="w-3 h-3" /> MEJOR HORA
                  </button>
                )}
              </div>
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
